/**
 * Bilt profile-completeness (client mirror of backend/includes/vehicle_completeness.php).
 *
 * A vehicle's completeness is a 7-item checklist; `percent` = filled / total.
 * A profile is "complete" at 100%. Core identity (year/make/model/trim) is
 * required at creation and therefore not scored. `build_status` is a physical
 * build state and is intentionally NOT part of completeness.
 *
 * Keep the item list and threshold in sync with the PHP helper.
 */

export type VehicleCompletenessItem =
  | 'engine'
  | 'transmission'
  | 'drivetrain'
  | 'chassis'
  | 'description'
  | 'photo'
  | 'modification';

export interface VehicleCompletenessInput {
  engine?: string | null;
  transmission?: string | null;
  drivetrain?: string | null;
  chassis?: string | null;
  description?: string | null;
  /** Number of photos attached to the vehicle. */
  imageCount?: number;
  /** Number of modifications on the vehicle. */
  modificationCount?: number;
}

export interface VehicleCompletenessResult {
  percent: number;
  completed: boolean;
  filled: number;
  total: number;
  missing: VehicleCompletenessItem[];
}

const hasText = (value?: string | null): boolean =>
  typeof value === 'string' && value.trim() !== '';

export function getVehicleCompleteness(
  input: VehicleCompletenessInput
): VehicleCompletenessResult {
  const items: Record<VehicleCompletenessItem, boolean> = {
    engine: hasText(input.engine),
    transmission: hasText(input.transmission),
    drivetrain: hasText(input.drivetrain),
    chassis: hasText(input.chassis),
    description: hasText(input.description),
    photo: (input.imageCount ?? 0) > 0,
    modification: (input.modificationCount ?? 0) > 0,
  };

  const entries = Object.entries(items) as [VehicleCompletenessItem, boolean][];
  const total = entries.length;
  const filled = entries.filter(([, ok]) => ok).length;
  const missing = entries.filter(([, ok]) => !ok).map(([key]) => key);

  return {
    percent: total > 0 ? Math.round((filled / total) * 100) : 0,
    completed: total > 0 && filled === total,
    filled,
    total,
    missing,
  };
}
